// ParameterRegistry: collects dynamic variables from providers and maintains a stable mapping
// to pixel indices for efficient real-time visualization.

export type ParameterType = 'numeric' | 'categorical';

export interface NumericParameter {
    id: string; // stable id
    name: string; // display name
    category: string;
    type: 'numeric';
    value: number;
    min?: number;
    max?: number;
}

export interface CategoricalParameter {
    id: string;
    name: string;
    category: string;
    type: 'categorical';
    value: string; // category key
}

export type Parameter = NumericParameter | CategoricalParameter;

export type ParameterDescriptor = Parameter & {
    lastValue?: number | string;
    lastChangeAt?: number;
};

export type ParameterProvider = () => Parameter[];

export class ParameterRegistry {
    private providers: ParameterProvider[] = [];
    private idToIndex: Map<string, number> = new Map();
    private descriptors: ParameterDescriptor[] = [];

    // Exponential moving bounds for numeric normalization (for visual stability)
    private idToMinEma: Map<string, number> = new Map();
    private idToMaxEma: Map<string, number> = new Map();
    private boundsAlpha: number = 0.05;

    // Cached layout size (square grid side length)
    private gridSide: number = 0;

    registerProvider(provider: ParameterProvider): void {
        this.providers.push(provider);
    }

    clearProviders(): void {
        this.providers = [];
    }

    getParameters(): ParameterDescriptor[] {
        return this.descriptors;
    }

    getGridSide(): number {
        return this.gridSide;
    }

    update(): void {
        const collected: Parameter[] = [];
        for (let i = 0; i < this.providers.length; i++) {
            const params = this.providers[i]();
            if (params && params.length) {
                for (let j = 0; j < params.length; j++) collected.push(params[j]);
            }
        }

        // Stable id → index mapping
        for (let k = 0; k < collected.length; k++) {
            const p = collected[k];
            if (!this.idToIndex.has(p.id)) {
                this.idToIndex.set(p.id, this.idToIndex.size);
            }
        }

        // Build descriptors array ordered by stable index
        const ordered: ParameterDescriptor[] = new Array(this.idToIndex.size);
        for (let k = 0; k < collected.length; k++) {
            const p = collected[k];
            const idx = this.idToIndex.get(p.id)!;
            const prev = this.descriptors[idx];
            const desc: ParameterDescriptor = { ...p } as ParameterDescriptor;
            if (prev) {
                desc.lastValue = prev.value as any;
                if (prev.value !== p.value) desc.lastChangeAt = performance.now();
                else desc.lastChangeAt = prev.lastChangeAt;
            } else {
                desc.lastChangeAt = performance.now();
            }
            // Update EMA bounds for numeric
            if (p.type === 'numeric') {
                const prevMin = this.idToMinEma.get(p.id);
                const prevMax = this.idToMaxEma.get(p.id);
                const hardMin = p.min ?? p.value;
                const hardMax = p.max ?? p.value;
                const minEma = prevMin == null ? hardMin : (1 - this.boundsAlpha) * prevMin + this.boundsAlpha * Math.min(p.value, prevMin);
                const maxEma = prevMax == null ? hardMax : (1 - this.boundsAlpha) * prevMax + this.boundsAlpha * Math.max(p.value, prevMax);
                this.idToMinEma.set(p.id, Math.min(minEma, hardMin));
                this.idToMaxEma.set(p.id, Math.max(maxEma, hardMax));
            }
            ordered[idx] = desc;
        }

        this.descriptors = ordered;
        // Update grid side as ceil(sqrt(N))
        const n = this.descriptors.length;
        this.gridSide = n > 0 ? Math.ceil(Math.sqrt(n)) : 0;
    }

    // Normalized value 0..1 using EMA bounds
    getNormalized(id: string, raw: number): number {
        const min = this.idToMinEma.get(id);
        const max = this.idToMaxEma.get(id);
        if (min == null || max == null || max <= min) return 0.5;
        const t = (raw - min) / (max - min);
        return Math.max(0, Math.min(1, t));
    }
}


